import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Spinner } from '../Spinner';

export const buttonVariants = cva(
  // Base styles - common to all variants
  'inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 select-none whitespace-nowrap',
  {
    variants: {
      variant: {
        primary: 'bg-primary-600 text-primary-foreground shadow hover:bg-primary-700 active:bg-primary-800',
        secondary: 'bg-secondary-600 text-white shadow hover:bg-secondary-700 active:bg-secondary-800',
        accent: 'bg-accent-500 text-white shadow hover:bg-accent-600 active:bg-accent-700',
        ghost: 'hover:bg-muted hover:text-foreground active:bg-muted',
        danger: 'bg-error-600 text-white shadow hover:bg-error-700 active:bg-error-800',
        destructive: 'bg-error-600 text-white shadow hover:bg-error-700 active:bg-error-800',
        error: 'bg-error-600 text-white shadow hover:bg-error-700 active:bg-error-800',
        success: 'bg-success-600 text-white shadow hover:bg-success-700 active:bg-success-800',
        outline: 'border border-input bg-background hover:bg-muted hover:text-foreground',
        dashed: 'border-2 border-dashed border-border bg-background hover:bg-muted hover:border-border text-foreground',
        link: 'text-primary-600 underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-sm rounded-md',
        md: 'h-10 px-4 text-base rounded-md',
        lg: 'h-12 px-6 text-lg rounded-md',
        icon: 'h-10 w-10 rounded-md',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);


export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  loading?: boolean; // Alias for isLoading
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  icon?: React.ReactNode; // Alias for leftIcon
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      isLoading = false,
      loading = false,
      leftIcon,
      rightIcon,
      icon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    // Handle aliases
    const actualLoading = isLoading || loading;
    const actualLeftIcon = leftIcon || icon;
    
    // Remove the aliased props from spreading to DOM
    const { loading: _, icon: __, ...restProps } = props as any;
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        disabled={actualLoading || disabled}
        {...restProps}
      >
        <>
          {actualLoading ? (
            <Spinner size="sm" className="mr-2" />
          ) : (
            actualLeftIcon && <span className="mr-2">{actualLeftIcon}</span>
          )}
          {children}
          {!actualLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      </button>
    );
  }
);

Button.displayName = 'Button';
